import { useEffect, useRef, useState } from "react";
import { motion, useSpring, useTransform } from "framer-motion";
import { ArrowUp, ArrowDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useOrgSettings } from "@/lib/org-settings-context";

interface AnimatedBalanceProps {
  value: number;
  className?: string;
  showTrend?: boolean;
  trend?: "up" | "down" | "neutral";
  trendValue?: number;
  size?: "sm" | "md" | "lg" | "xl";
}

export function AnimatedBalance({
  value,
  className,
  showTrend = false,
  trend = "neutral",
  trendValue = 0,
  size = "lg",
}: AnimatedBalanceProps) {
  const { formatCurrency: orgFormatCurrency } = useOrgSettings();
  const [displayValue, setDisplayValue] = useState(0);
  const prevValue = useRef(value);

  // Spring animation for smooth counting
  const spring = useSpring(0, {
    stiffness: 100,
    damping: 30,
    mass: 1,
  });

  useEffect(() => {
    spring.set(value);
    
    const unsubscribe = spring.on("change", (latest) => {
      setDisplayValue(latest);
    });

    return () => unsubscribe();
  }, [value, spring]);

  const formatCurrency = (val: number) => {
    return orgFormatCurrency(val);
  };

  const sizeClasses = {
    sm: "text-2xl",
    md: "text-3xl",
    lg: "text-4xl md:text-5xl",
    xl: "text-5xl md:text-6xl",
  };

  return (
    <div className={cn("flex items-center gap-3", className)}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn("font-bold tabular-nums", sizeClasses[size])}
      >
        {formatCurrency(displayValue)}
      </motion.div>

      {showTrend && trend !== "neutral" && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className={cn(
            "flex items-center gap-1 rounded-full px-2 py-1 text-sm font-medium",
            trend === "up"
              ? "bg-green-500/10 text-green-600"
              : "bg-red-500/10 text-red-600"
          )}
        >
          {trend === "up" ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )}
          <span>{Math.abs(trendValue)}%</span>
        </motion.div>
      )}
    </div>
  );
}
